// ── CAPR S3 — the REVIEW-CADENCE vocabulary, declared ONCE ────────────────────────────────────
//
// How often a control's implementation status is re-assessed, and the derived label that says where
// a control stands against that schedule. The database CHECKs (V1087) are the backstop; these are the
// names the api writes and every surface reads. Nothing restates these lists.
//
// ⚠ THIS IS THE REVIEW AXIS ONLY. It is not the catalogue's authored advice about evidence freshness,
//   and it is not the platform's schedule for re-checking evidence at its source. Those are separate
//   facts with separate owners (BR-C8): no value here is read by either, and no value of theirs is
//   read here. The review interval is its own month-based map, evaluated in SQL (cts_review_interval,
//   V1087), never a number of days.

/** ⚠ The PERIODIC cadences — the only values the ORGANIZATION DEFAULT may take (V1087 CHECK). */
export const CTS_REVIEW_PERIODIC_CADENCES = [
  'monthly',
  'quarterly',
  'semiannual',
  'annual',
  'biennial',
] as const;
export type CtsReviewPeriodicCadence = typeof CTS_REVIEW_PERIODIC_CADENCES[number];

/**
 * ⚠ The NON-PERIODIC cadences — a per-control OVERRIDE only. A control on one of these has no next
 * review date and is labelled "No scheduled review"; it is assessed when someone starts an assessment.
 */
export const CTS_REVIEW_NON_PERIODIC_CADENCES = ['as_needed', 'event_driven'] as const;

/** Every value a per-control override may take. */
export const CTS_REVIEW_CADENCES = [...CTS_REVIEW_PERIODIC_CADENCES, ...CTS_REVIEW_NON_PERIODIC_CADENCES] as const;
export type CtsReviewCadence = typeof CTS_REVIEW_CADENCES[number];

/** The organization default every tenant starts on (FR-N6, BR-C1) — labelled a default, never a framework requirement. */
export const CTS_REVIEW_DEFAULT_CADENCE: CtsReviewPeriodicCadence = 'annual';

export const CTS_REVIEW_CADENCE_LABEL: Record<CtsReviewCadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Semiannual',
  annual: 'Annual',
  biennial: 'Every two years',
  as_needed: 'As needed',
  event_driven: 'Event-driven',
};

/** Where a control's effective cadence came from. */
export const CTS_REVIEW_CADENCE_SOURCES = ['override', 'organization_default'] as const;
export type CtsReviewCadenceSource = typeof CTS_REVIEW_CADENCE_SOURCES[number];

/**
 * ⚠ THE DERIVED REVIEW LABEL — computed at read, never stored (BR-C3/BR-C4). First match wins:
 *   not_yet_assessed     no approved decision (so a never-assessed control is never overdue, BR-C5)
 *   no_scheduled_review  the effective cadence is not periodic
 *   overdue              the next review date has passed
 *   due_soon             the next review date is within 30 days
 *   current              otherwise
 *
 * ⚠ "Assessment in progress" is NOT a label here. It is a separate field (`in_progress`), so open work
 *   can never hide that a control is overdue — both facts are shown together.
 */
export const CTS_REVIEW_LABELS = [
  'not_yet_assessed',
  'no_scheduled_review',
  'overdue',
  'due_soon',
  'current',
] as const;
export type CtsReviewLabel = typeof CTS_REVIEW_LABELS[number];

export const CTS_REVIEW_LABEL_TEXT: Record<CtsReviewLabel, string> = {
  not_yet_assessed: 'Not yet assessed',
  no_scheduled_review: 'No scheduled review',
  overdue: 'Overdue',
  due_soon: 'Due soon',
  current: 'Current',
};

/** The rendered name of the separate open-work fact. */
export const CTS_ASSESSMENT_IN_PROGRESS_LABEL = 'Assessment in progress';

/** "Due soon" is the 30 days before the next review date (BR-C4). */
export const CTS_REVIEW_DUE_SOON_DAYS = 30;

/** The longest rationale an override may carry. */
export const CTS_REVIEW_OVERRIDE_RATIONALE_MAX = 2000;

/** "Organization default: Annual" / "Override: Quarterly" — the cadence with its source (UX-02). */
export function ctsReviewCadenceWithSource(cadence: CtsReviewCadence, source: CtsReviewCadenceSource): string {
  return `${source === 'override' ? 'Override' : 'Organization default'}: ${CTS_REVIEW_CADENCE_LABEL[cadence]}`;
}
